import { useState } from 'react'
import { CButton, CButtonGroup, CContainer } from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilBolt, cilClock, cilMemory, cilSpeedometer } from '@coreui/icons'
import { useQuery } from '@tanstack/react-query'
import { publicApi, apiErrorMessage } from '../../api/client'
import { DemoBanner } from '../../components/common/DemoBanner'
import { ErrorState, LoadingState } from '../../components/common/AsyncState'
import { MetricCard } from '../../components/common/MetricCard'
import { SectionHeader } from '../../components/common/SectionHeader'
import { ActivityComposition } from '../../components/public/ActivityComposition'
import { MachineStateCard } from '../../components/public/MachineStateCard'
import { UtilizationChart } from '../../components/public/UtilizationChart'
import { formatMemory, formatNumber, formatPercent } from '../../utils/format'

const ranges = ['24h', '7d', '30d', '90d'] as const

export function ObservatoryPage() {
  const [range, setRange] = useState<(typeof ranges)[number]>('30d')
  const query = useQuery({
    queryKey: ['public', 'overview', range],
    queryFn: () => publicApi.overview(range),
    staleTime: 30_000,
    refetchInterval: range === '24h' ? 30_000 : 60_000,
  })

  return (
    <>
      {query.data?.demoMode && <DemoBanner />}
      <section className="page-hero page-hero--observatory">
        <CContainer xxl>
          <span className="eyebrow">H100 Live</span>
          <h1>Current condition and utilization, without guesswork.</h1>
          <p>
            Observed processing load, memory activity and machine states are reported separately from
            reservations, services and ready capacity.
          </p>
        </CContainer>
      </section>
      <section className="section-space--tight">
        <CContainer xxl>
          {query.isLoading && <LoadingState />}
          {query.isError && (
            <ErrorState message={apiErrorMessage(query.error)} retry={() => void query.refetch()} />
          )}
          {query.data && (
            <>
              <div className="observatory-toolbar">
                <div>
                  <strong>History range</strong>
                  <span>Times shown in ICT</span>
                </div>
                <CButtonGroup role="group" aria-label="Select history range">
                  {ranges.map((item) => (
                    <CButton
                      key={item}
                      color={range === item ? 'primary' : 'light'}
                      onClick={() => setRange(item)}
                    >
                      {item}
                    </CButton>
                  ))}
                </CButtonGroup>
              </div>
              <MachineStateCard overview={query.data} />
              <div className="metric-grid metric-grid--four">
                <MetricCard
                  label="GPU processing activity"
                  value={formatPercent(query.data.current?.gpuUtilizationPct, 1)}
                  helper="Live observed value"
                  icon={<CIcon icon={cilSpeedometer} />}
                  emphasis
                />
                <MetricCard
                  label="Memory footprint"
                  value={formatMemory(query.data.current?.memoryUsedMiB)}
                  helper={`${formatPercent(query.data.current?.memoryUtilizationPct, 1)} memory activity`}
                  icon={<CIcon icon={cilMemory} />}
                />
                <MetricCard
                  label="Power draw"
                  value={
                    query.data.current?.powerW == null
                      ? '—'
                      : `${formatNumber(query.data.current.powerW)} W`
                  }
                  helper="Live board power measurement"
                  icon={<CIcon icon={cilBolt} />}
                />
                <MetricCard
                  label="Observed history"
                  value={`${formatNumber(query.data.summary.observedHours, 0)} h`}
                  helper={`Selected ${range} window`}
                  icon={<CIcon icon={cilClock} />}
                />
              </div>

              <div className="data-story-grid mt-4">
                <article className="panel panel--wide">
                  <div className="panel__header">
                    <div>
                      <span className="eyebrow">Time series</span>
                      <h2>GPU and memory activity</h2>
                    </div>
                  </div>
                  <UtilizationChart series={query.data.series} height={350} />
                </article>
                <article className="panel">
                  <div className="panel__header">
                    <div>
                      <span className="eyebrow">Machine states</span>
                      <h2>Activity composition</h2>
                    </div>
                  </div>
                  <ActivityComposition items={query.data.stateBreakdown} />
                </article>
              </div>

              <div className="mt-5">
                <SectionHeader
                  eyebrow="Interpretation"
                  title="What the numbers do—and do not—claim"
                  description="A memory-bound workload may do useful work at lower processing utilization. A model service can remain available with intermittent load. Ready capacity is a positive operational state, but it is not counted as computation."
                />
                <div className="definition-grid">
                  <article>
                    <strong>Availability</strong>
                    <p>
                      {formatPercent(query.data.summary.availabilityPct, 1)} of observed samples were
                      healthy states.
                    </p>
                  </article>
                  <article>
                    <strong>Compute-equivalent time</strong>
                    <p>
                      {formatNumber(query.data.summary.computeEquivalentGpuHours, 1)} GPU-hours after
                      weighting observed time by processing utilization.
                    </p>
                  </article>
                  <article>
                    <strong>Service presence</strong>
                    <p>
                      {query.data.current?.serviceCount ?? 0} approved service contexts are currently
                      reported by the collector.
                    </p>
                  </article>
                </div>
              </div>
            </>
          )}
        </CContainer>
      </section>
    </>
  )
}
